//@author: mandy
import { DataAccess } from "../../../dataaccess/data.access";
import { S25Const } from "../../../util/s25-const";
import { S25Util } from "../../../util/s25-util";
import { SearchService } from "../search.service";
import { RoleService } from "../../role.service";
import { jSith } from "../../../util/jquery-replacement";
import { Cache } from "../../../decorators/cache.decorator";
import { Timeout } from "../../../decorators/timeout.decorator";
import { GroupService } from "../../group.service";
import { EventService } from "../../event.service";
import { Item } from "../../../pojo/Item";
import { BuildingService } from "../../building.service";
import { Rules } from "../../../modules/s25-rule-tree/s25.rule.const";
import { StandardScheduleService } from "../../standard.schedule.service";
import { Proto } from "../../../pojo/Proto";
import NumericalBoolean = Proto.NumericalBoolean;
import { BpeService } from "../../../modules/bpe/bpe.service";
import { MasterDefinitionTagsService } from "../../master.definitions/master.definition.tags.service";
import { MasterDefTag } from "../../../pojo/MasterDefTag";
import { Question, StudentHousingService } from "../../../modules/student-housing/student.housing.service";
import { DataIngestionService, IngestionFieldDefinition } from "../../../modules/data-ingestion/data.ingestion.service";
import { INGESTION_FIELD_TYPE_ID } from "../../../modules/data-ingestion/data.ingestion.const";
import { WorkflowService } from "../../../modules/workflow/service/workflow.service";
import { Report } from "../../../pojo/Report";
import ObjectType = Report.ObjectType;
import { Workflow } from "../../../modules/workflow/service/Workflow";
import { CustomAttributes } from "../../../pojo/CustomAttributes";
import { BudgetService } from "../../../modules/student-engagement/budget/budget.service";
import { Budget } from "../../../modules/student-engagement/budget/Budget";
import { ElectionApi } from "../../../modules/student-engagement/elections/election.api";
import type { S25ItemI } from "../../../pojo/S25ItemI";
import type { Organization } from "../../../pojo/Organization";
import { SeSearchService } from "../../../modules/student-engagement/search/se.search.service";

export interface CustomAttributeResponseItem {
    itemId: number;
    itemName: string;
    itemTypeId: CustomAttributes.Type;
    sort_order: number;
    multi_val?: NumericalBoolean;
}

export class SearchCriteriaService {
    public static setItemTypeId(obj: any, itemTypeId: number) {
        const items = S25Util.array.isArray(obj) ? obj : (obj && obj.searchCriteria && obj.searchCriteria.item) || [];
        jSith.forEach(items, function (key: any, item: any) {
            item.itemTypeId = itemTypeId;
        });
        return obj;
    }

    public static errorHandler<Items extends any[] = SearchCriteriaItem[]>(err: any): Items {
        return [] as unknown as Items;
    }

    public static composeUrl(
        path: any,
        caller: any,
        useSecurity?: any,
        eventTypeId?: number,
        activeOnly?: any,
        customFilterValue?: string,
    ) {
        let searchQuery = "";

        if (S25Util.toBool(activeOnly)) {
            //only return active items
            searchQuery = S25Util.addQueryParam("active=T", searchQuery);
        }

        if (S25Util.toBool(useSecurity)) {
            //only return items for which the user has security (eg, via config ev_types and rose security allowed_event_types pref)
            searchQuery = S25Util.addQueryParam("all_types=F", searchQuery);
        }

        if (eventTypeId) {
            //further filter by event type (evt_* tables)
            searchQuery = S25Util.addQueryParam("event_type_id=" + eventTypeId, searchQuery);
        }

        if (customFilterValue) {
            searchQuery = S25Util.addQueryParam(customFilterValue, searchQuery);
        }

        path = "/search-criteria" + path;
        path = path + (searchQuery ? (path.indexOf("?") > -1 ? searchQuery : "?" + searchQuery.substring(1)) : "");

        return DataAccess.injectCaller(path, caller);
    }

    public static handlePagination(
        cacheId: number,
        pageNum: number,
        pageSize: number,
        filter: string | undefined,
        filterFavorites: any,
        domainFilter: any,
        customFilterValue: any,
        url: any,
    ) {
        let queryString = "";
        const isFilter = S25Util.isDefined(filter) && filter.trim() !== "";
        cacheId = cacheId || 0;
        pageSize = pageSize || 100;
        pageNum = pageNum || 0;

        if (cacheId >= 0) {
            //negative cacheId indicates to force no pagination
            queryString += "&obj_cache_accl=" + cacheId;
            queryString += "&page=" + pageNum;
            queryString += "&page_size=" + pageSize;
        } else if (pageSize) {
            queryString += "&page_size=" + pageSize;
        }

        queryString += filterFavorites === true ? "&favorite=T" : "";
        queryString +=
            domainFilter && domainFilter.chosen && domainFilter.chosen.itemId > -999
                ? domainFilter.prefix + domainFilter.chosen.itemId
                : "";
        queryString += isFilter ? "&name=" + filter : "";
        queryString += customFilterValue || "";
        return url + "?" + queryString.substring(1);
    }

    @Timeout
    @Cache({ immutable: true, targetName: "SearchCriteriaService" })
    public static getCustomAttributeItems(
        typeId: Item.Id,
        useSecurity?: any,
        eventTypeId?: any,
    ): Promise<CustomAttributeResponseItem[]> {
        //note: itemTypeId is cust atrb type (B, N, F, X, etc), itemName is cust atrb name, itemId is cust atrb id
        const url = "/customattributes.json?itemTypeId=" + typeId;

        return DataAccess.get(
            SearchCriteriaService.composeUrl(
                url,
                "SearchCriteriaService.getCustomAttributes",
                useSecurity,
                eventTypeId,
                undefined,
                undefined,
            ),
        ).then(function (data) {
            const items = data?.searchCriteria?.item || [];
            for (const item of items) item.isCustomAttribute = true;
            return items;
        }, SearchCriteriaService.errorHandler);
    }

    @Timeout
    public static optSearchesToItemList(searches: any) {
        const ret: any = [],
            map: any = {};
        searches = searches && searches.opt;
        jSith.forEach(searches, function (key: any, s: any) {
            if (s && S25Util.isDefined(s.itemId) && !map[s.itemId]) {
                map[s.itemId] = true;
                ret.push({
                    itemId: s.itemId,
                    itemName: s.txt,
                    favorite: s.favorite,
                    isOwner: s.isOwner,
                    val: s.val,
                });
            }
        });
        return ret;
    }

    //extract items from search criteria return data
    public static extractItems(data: any) {
        if (S25Util.array.isArray(data)) {
            return data;
        }
        const item = data?.searchCriteria?.item;
        if (S25Util.array.isArray(item)) {
            return item;
        }
        if (item != null && typeof item === "object") {
            return [item];
        }
        return [];
    }

    //security groups
    @Timeout
    @Cache({ immutable: true, targetName: "SearchCriteriaService" })
    public static getSecurityGroups() {
        return GroupService.getGroupItems().then(function (items) {
            return SearchCriteriaService.setItemTypeId(items, 99);
        }, SearchCriteriaService.errorHandler);
    }

    // EventType Reports
    @Timeout
    @Cache({ immutable: true, targetName: "SearchCriteriaService" })
    public static async getEventTypeReports(): Promise<Report.Obj.Normalized[]> {
        let [items, err] = await S25Util.Maybe(EventService.getEventTypeReports());
        if (err) return SearchCriteriaService.errorHandler(err);

        const objectTypes = new Set<number>([
            ObjectType.Event,
            ObjectType.EventDocument,
            ObjectType.PaymentDocument,
            ObjectType.ReservationDocument,
            ObjectType.OrganizationDocument,
        ]); // ANG-4852
        items = items.filter((item) => objectTypes.has(Number(item.object_type)));
        return Report.Obj.normalizeReports(items, true);
    }

    //events
    @Timeout
    @Cache({ immutable: true, targetName: "SearchCriteriaService" })
    public static getEventRequirements(useSecurity: any, eventTypeId?: number, customFilterValue?: string) {
        useSecurity = useSecurity || S25Util.isDefined(eventTypeId); //set to true if event type id sent in so that downsteam code handles it (all types set to F)
        return DataAccess.get(
            SearchCriteriaService.composeUrl(
                "/event/requirements.json",
                "SearchCriteriaService.getEventRequirements",
                useSecurity,
                eventTypeId,
                null,
                customFilterValue,
            ),
        ).then(function (data) {
            //ANG-4149 only show types 6/7 for requirements
            const resp = (data && data.searchCriteria && data.searchCriteria.item) || [];
            //When useSecurity is false, resp body won't include an itemTypeId so do not filter
            return useSecurity || S25Util.contains(customFilterValue, "all_types=F")
                ? resp.filter((item: any) => {
                      return [6, 7].indexOf(S25Util.parseInt(item.itemTypeId)) > -1;
                  })
                : resp;
        }, SearchCriteriaService.errorHandler);
    }

    @Timeout
    public static getEventSearches() {
        return SearchService.getStrictSearches(1).then(function (searches) {
            return SearchCriteriaService.optSearchesToItemList(searches);
        });
    }

    @Timeout
    @Cache({ immutable: true, targetName: "SearchCriteriaService", expireTimeMs: 60000 })
    public static getEvents(
        cacheId: number,
        pageNum: number,
        pageSize: number,
        filter: any,
        filterFavorites: any,
        domainFilter: any,
        customFilterValue: any,
    ) {
        return DataAccess.get(
            SearchCriteriaService.composeUrl(
                SearchCriteriaService.handlePagination(
                    cacheId,
                    pageNum,
                    pageSize,
                    filter,
                    filterFavorites,
                    domainFilter,
                    customFilterValue,
                    "/event/events.json",
                ),
                "SearchCriteriaService.getEvents",
                undefined,
                undefined,
                undefined,
                undefined,
            ),
        ).then(function (data) {
            return SearchCriteriaService.setItemTypeId(data || [], 1);
        }, SearchCriteriaService.errorHandler);
    }

    @Timeout
    @Cache({ immutable: true, targetName: "SearchCriteriaService" })
    public static getEventCategories(useSecurity?: number, eventTypeId?: number, omitReadOnly?: boolean) {
        return DataAccess.get(
            SearchCriteriaService.composeUrl(
                "/event/categories.json",
                "SearchCriteriaService.getEventCategories",
                useSecurity,
                eventTypeId,
                undefined,
                undefined,
            ),
        ).then(function (data) {
            const items = (data && data.searchCriteria && data.searchCriteria.item) || [];
            return omitReadOnly
                ? items
                : items.filter((item: { tags: MasterDefTag.MdTag[] }) =>
                      MasterDefinitionTagsService.isItemReadOnly(item?.tags),
                  );
        }, SearchCriteriaService.errorHandler);
    }

    @Timeout
    @Cache({ immutable: true, targetName: "SearchCriteriaService" })
    public static getEventTypes(useSecurity: any, activeOnly: any, formConfigOnly?: any) {
        const customFilterValue = S25Util.toBool(formConfigOnly) ? "form_config=T" : undefined;
        return DataAccess.get(
            SearchCriteriaService.composeUrl(
                "/event/types.json",
                "SearchCriteriaService.getEventTypes",
                useSecurity,
                undefined,
                activeOnly,
                customFilterValue,
            ),
        ).then(function (data) {
            data = (data && data.searchCriteria && data.searchCriteria.item) || [];
            data = SearchCriteriaService.setItemTypeId(data, 7);
            return data.filter(function (item: any) {
                return item.itemName && (item.itemName + "").toLowerCase() !== "(unspecified)";
            });
        }, SearchCriteriaService.errorHandler);
    }

    @Timeout
    @Cache({ immutable: true, targetName: "SearchCriteriaService" })
    public static getEventCabinets() {
        return DataAccess.get(
            SearchCriteriaService.composeUrl(
                "/event/cabinets.json",
                "SearchCriteriaService.getEventCabinets",
                undefined,
                undefined,
                undefined,
                undefined,
            ),
        ).then(function (data) {
            return (data && data.searchCriteria && data.searchCriteria.item) || [];
        }, SearchCriteriaService.errorHandler);
    }

    @Timeout
    @Cache({ immutable: true, targetName: "SearchCriteriaService" })
    public static getEventStates(): Promise<{ itemId: number; itemName: string }[]> {
        return DataAccess.get(
            SearchCriteriaService.composeUrl(
                "/event/states.json",
                "SearchCriteriaService.getEventStates",
                undefined,
                undefined,
                undefined,
                undefined,
            ),
        ).then(function (data) {
            return (data && data.searchCriteria && data.searchCriteria.item) || [];
        }, SearchCriteriaService.errorHandler);
    }

    @Timeout
    public static getAssignedEventStates() {
        return SearchCriteriaService.getEventStates().then(function (items: any) {
            return (
                items &&
                items.length &&
                items.filter(function (item: any) {
                    return [1, 2, 3].indexOf(item.itemId) > -1;
                })
            );
        });
    }

    public static getRolesFiltered(
        type: Item.Ids,
    ): Promise<{ role_name: string; role_id: string; defn_state: boolean }[]> {
        const typeName = S25Const.itemId2Name[type] || type;
        const service =
            typeName === "event" ? SearchCriteriaService.getEventRoles : SearchCriteriaService.getOrganizationRoles;
        return service(true, false).then(function (roles) {
            return (
                roles &&
                roles.map((r: any) => {
                    return {
                        role_name: r.itemName,
                        role_id: r.itemId,
                        defn_state: !r.isInactive,
                    };
                })
            );
        });
    }

    @Timeout
    @Cache({ immutable: true, targetName: "SearchCriteriaService" })
    public static getEventRoles(useSecurity: any, typedRolesOnly: boolean) {
        if (typedRolesOnly) {
            return RoleService.getEventTypeRoles(undefined).then(function (roles: any) {
                return roles.map(function (role: any) {
                    return {
                        itemId: role.role_id,
                        itemName: role.role_name,
                    };
                });
            });
        } else {
            return DataAccess.get(
                SearchCriteriaService.composeUrl(
                    "/event/roles.json",
                    "SearchCriteriaService.getEventRoles",
                    useSecurity,
                    undefined,
                    undefined,
                    undefined,
                ),
            ).then(function (data) {
                return (data && data.searchCriteria && data.searchCriteria.item) || [];
            }, SearchCriteriaService.errorHandler);
        }
    }

    @Timeout
    public static getEventCustomAttributes(useSecurity?: boolean) {
        return SearchCriteriaService.getCustomAttributeItems(1, useSecurity, null);
    }

    @Timeout
    public static getFolders(
        cacheId: any,
        pageNum: number,
        pageSize: number,
        filter: any,
        filterFavorites: any,
        domainFilter: any,
        customFilterValue: any,
    ) {
        customFilterValue = customFilterValue || "";
        customFilterValue += "&node_type=F";
        return DataAccess.get(
            SearchCriteriaService.composeUrl(
                SearchCriteriaService.handlePagination(
                    cacheId,
                    pageNum,
                    pageSize,
                    filter,
                    filterFavorites,
                    domainFilter,
                    customFilterValue,
                    "/event/events.json",
                ),
                "SearchCriteriaService.getFolders",
                undefined,
                undefined,
                undefined,
                undefined,
            ),
        ).then(function (data) {
            return SearchCriteriaService.setItemTypeId(data || [], 1);
        }, SearchCriteriaService.errorHandler);
    }

    //locations
    @Timeout
    @Cache({ immutable: true, targetName: "SearchCriteriaService" })
    public static getSpaceCategories(omitReadOnly?: boolean) {
        return DataAccess.get(
            SearchCriteriaService.composeUrl(
                "/space/categories.json",
                "SearchCriteriaService.getSpaceCategories",
                true,
                undefined,
                undefined,
                undefined,
            ),
        ).then(function (data) {
            const items = (data && data.searchCriteria && data.searchCriteria.item) || [];
            return omitReadOnly
                ? items
                : items.filter((item: { tags: MasterDefTag.MdTag[] }) =>
                      MasterDefinitionTagsService.isItemReadOnly(item?.tags),
                  );
        }, SearchCriteriaService.errorHandler);
    }

    @Timeout
    @Cache({ immutable: true, targetName: "SearchCriteriaService" })
    public static getSpaceLayouts(eventTypeId?: number, activeOnly?: any, customFilterValue?: any) {
        return DataAccess.get(
            SearchCriteriaService.composeUrl(
                "/space/layouts.json",
                "SearchCriteriaService.getSpaceLayouts",
                true,
                undefined,
                undefined,
                customFilterValue,
            ),
        ).then(function (data) {
            return (data && data.searchCriteria && data.searchCriteria.item) || [];
        }, SearchCriteriaService.errorHandler);
    }

    @Timeout
    public static getLocationSearches() {
        return SearchService.getStrictSearches(4).then(function (searches) {
            return SearchCriteriaService.optSearchesToItemList(searches);
        });
    }

    @Timeout
    @Cache({ immutable: true, targetName: "SearchCriteriaService", expireTimeMs: 60000 })
    public static getLocations(
        cacheId: number,
        pageNum: number,
        pageSize: number,
        filter: any,
        filterFavorites: any,
        domainFilter: any,
        customFilterValue: any,
    ) {
        return DataAccess.get(
            SearchCriteriaService.composeUrl(
                SearchCriteriaService.handlePagination(
                    cacheId,
                    pageNum,
                    pageSize,
                    filter,
                    filterFavorites,
                    domainFilter,
                    customFilterValue,
                    "/space/locations.json",
                ),
                "SearchCriteriaService.getLocations",
                undefined,
                undefined,
                undefined,
                undefined,
            ),
        ).then(function (data) {
            return SearchCriteriaService.setItemTypeId(data || [], 4);
        }, SearchCriteriaService.errorHandler);
    }

    @Timeout
    @Cache({ immutable: true, targetName: "SearchCriteriaService" })
    public static getLocationRelatedTypes() {
        return jSith.when([
            { itemName: "Subdivision Of", itemId: 1 },
            { itemName: "Close To", itemId: 2 },
            { itemName: "Blocked By", itemId: 3 },
            { itemName: "Also Assign", itemId: 4 },
            { itemName: "Divides Into", itemId: 5 },
        ]);
    }

    @Timeout
    public static getLocationCustomAttributes() {
        return SearchCriteriaService.getCustomAttributeItems(4, null, null);
    }

    @Timeout
    //organizations
    public static getOrganizationSearches() {
        return SearchService.getStrictSearches(2).then(function (searches) {
            return SearchCriteriaService.optSearchesToItemList(searches);
        });
    }

    @Timeout
    @Cache({ immutable: true, targetName: "SearchCriteriaService" })
    public static getOrganizationRatings() {
        return DataAccess.get(
            SearchCriteriaService.composeUrl(
                "/organization/ratings.json",
                "SearchCriteriaService.getOrganizationRatings",
                undefined,
                undefined,
                undefined,
                undefined,
            ),
        ).then(function (data) {
            return (data && data.searchCriteria && data.searchCriteria.item) || [];
        }, SearchCriteriaService.errorHandler);
    }

    @Timeout
    @Cache({ immutable: true, targetName: "SearchCriteriaService" })
    public static getOrganizationRoles(useSecurity?: boolean, activeOnly?: boolean) {
        return DataAccess.get<SearchCriteriaResponse>(
            SearchCriteriaService.composeUrl(
                "/organization/roles.json",
                "SearchCriteriaService.getOrganizationRoles",
                useSecurity,
                undefined,
                activeOnly,
                undefined,
            ),
        ).then(function (data) {
            const resp = (data && data.searchCriteria && data.searchCriteria.item) || [];
            return resp.map((role) => ({ ...role, isActive: !role.isInactive }));
        }, SearchCriteriaService.errorHandler);
    }

    @Timeout
    @Cache({ immutable: true, targetName: "SearchCriteriaService" })
    public static getOrganizationCategories() {
        return DataAccess.get(
            SearchCriteriaService.composeUrl(
                "/organization/categories.json",
                "SearchCriteriaService.getOrganizationCategories",
                true,
                undefined,
                undefined,
                undefined,
            ),
        ).then(function (data) {
            return (data && data.searchCriteria && data.searchCriteria.item) || [];
        }, SearchCriteriaService.errorHandler);
    }

    @Timeout
    @Cache({ immutable: true, targetName: "SearchCriteriaService" })
    public static getOrganizationTypes() {
        return DataAccess.get(
            SearchCriteriaService.composeUrl(
                "/organization/types.json",
                "SearchCriteriaService.getOrganizationTypes",
                true,
                undefined,
                undefined,
                undefined,
            ),
        ).then(function (data) {
            return (data && data.searchCriteria && data.searchCriteria.item) || [];
        }, SearchCriteriaService.errorHandler);
    }

    @Timeout
    @Cache({ immutable: true, targetName: "SearchCriteriaService", expireTimeMs: 60000 })
    public static getEventOrganizations(
        cacheId: number,
        pageNum: number,
        pageSize: number,
        filter: any,
        filterFavorites: any,
        domainFilter: any,
        customFilterValue: any,
    ) {
        return DataAccess.get(
            SearchCriteriaService.composeUrl(
                SearchCriteriaService.handlePagination(
                    cacheId,
                    pageNum,
                    pageSize,
                    filter,
                    filterFavorites,
                    domainFilter,
                    customFilterValue,
                    "/event/organizations.json",
                ),
                "SearchCriteriaService.getEventOrganizations",
                undefined,
                undefined,
                undefined,
                undefined,
            ),
        ).then(function (data) {
            return SearchCriteriaService.setItemTypeId(data || [], 2);
        }, SearchCriteriaService.errorHandler);
    }

    /**
     * Paginated (internally) Student Engagement organizations for multiselect — uses {@code /tile/organizations.json}
     * with the SE org custom attribute, not generic event organization search.
     * When {@code filterFavorites} is true, passes {@code favorite=T} (same convention as {@link SearchCriteriaService.handlePagination}).
     */
    @Timeout
    @Cache({ immutable: true, targetName: "SearchCriteriaService", expireTimeMs: 60000 })
    public static getStudentEngagementOrganizations(
        _cacheId: number,
        _pageNum: number,
        _pageSize: number,
        filter: any,
        filterFavorites: any,
        domainFilter: any,
        _customFilterValue: any,
    ): Promise<any> {
        return SearchCriteriaService.loadAllStudentEngagementOrganizationsForMultiselect(
            filter,
            filterFavorites === true,
        ).then(
            (items) =>
                SearchCriteriaService.setItemTypeId(
                    {
                        searchCriteria: {
                            item: items,
                            page_count: 1,
                            cache_id: 0,
                        },
                    },
                    2,
                ),
            SearchCriteriaService.errorHandler,
        );
    }

    /**
     * Full list of SE-flagged organizations for inline filter dropdowns (eager, no domain pick required).
     */
    public static fetchAllStudentEngagementOrganizationsEager(): Promise<S25ItemI<Item.Ids.Organization>[]> {
        return SearchCriteriaService.loadAllStudentEngagementOrganizationsForMultiselect(undefined, false) as Promise<
            S25ItemI<Item.Ids.Organization>[]
        >;
    }

    private static loadAllStudentEngagementOrganizationsForMultiselect(
        filter: any,
        onlyFavoriteOrganizations?: boolean,
    ): Promise<S25ItemI[]> {
        const nameFilter = typeof filter === "string" && filter.trim().length > 0 ? filter.trim() : undefined;
        const baseParams: Organization.WsQueryParams = {
            attribute_id: CustomAttributes.system.StudentEngagementOrg.id,
            attribute_value: "true",
            sort: "name",
            order: "asc",
        };
        if (nameFilter) {
            baseParams.name = nameFilter;
        }
        if (onlyFavoriteOrganizations) {
            baseParams.favorite = "T";
        }
        return (async () => {
            const byId = new Map<number, S25ItemI>();
            let page = 1;
            let paginateKey: string | null | undefined = "T";
            const perPage = 100;
            const maxPages = 100;
            while (page <= maxPages) {
                const response = await SeSearchService.getOrganizations(baseParams, {
                    page,
                    page_size: perPage,
                    paginate: paginateKey ?? "T",
                });
                const batch = response.items ?? [];
                for (const organization of batch) {
                    const organizationId = organization.itemId;
                    if (!byId.has(organizationId)) {
                        byId.set(organizationId, {
                            itemId: organizationId,
                            itemName: organization.itemName ?? organization.txt ?? String(organizationId),
                            itemTypeId: Item.Ids.Organization,
                        });
                    }
                }
                const pageCount = response.page_count ?? 1;
                if (page >= pageCount || batch.length === 0) {
                    break;
                }
                page += 1;
                paginateKey = response.paginate_key;
            }
            return [...byId.values()];
        })();
    }

    @Timeout
    public static getOrganizationCustomAttributes() {
        return SearchCriteriaService.getCustomAttributeItems(2, true, null);
    }

    //resources
    @Timeout
    @Cache({ immutable: true, targetName: "SearchCriteriaService" })
    public static getResourceCategories() {
        return DataAccess.get(
            SearchCriteriaService.composeUrl(
                "/resource/categories.json",
                "SearchCriteriaService.getResourceCategories",
                true,
                undefined,
                undefined,
                undefined,
            ),
        ).then(function (data) {
            return (data && data.searchCriteria && data.searchCriteria.item) || [];
        }, SearchCriteriaService.errorHandler);
    }

    @Timeout
    public static getResourceSearches() {
        return SearchService.getStrictSearches(6).then(function (searches) {
            return SearchCriteriaService.optSearchesToItemList(searches);
        });
    }

    @Timeout
    @Cache({ immutable: true, targetName: "SearchCriteriaService", expireTimeMs: 60000 })
    public static getResources(
        cacheId: number,
        pageNum: number,
        pageSize: number,
        filter: any,
        filterFavorites: any,
        domainFilter: any,
        customFilterValue: any,
        isIncludeQuantity?: any,
    ) {
        const url =
            SearchCriteriaService.composeUrl(
                SearchCriteriaService.handlePagination(
                    cacheId,
                    pageNum,
                    pageSize,
                    filter,
                    filterFavorites,
                    domainFilter,
                    customFilterValue,
                    "/resource/resources.json",
                ),
                "SearchCriteriaService.getResources",
                undefined,
                undefined,
                undefined,
                undefined,
            ) + (isIncludeQuantity ? DataAccess.composeParam("include", "quantity") : "");
        return DataAccess.get(url).then(function (data) {
            return SearchCriteriaService.setItemTypeId(data || [], 6);
        }, SearchCriteriaService.errorHandler);
    }

    @Timeout
    @Cache({ immutable: true, targetName: "SearchCriteriaService", expireTimeMs: 60000 })
    public static getResourcesQuantity(
        cacheId: number,
        pageNum: number,
        pageSize: number,
        filter: any,
        filterFavorites: any,
        domainFilter: any,
        customFilterValue: any,
    ) {
        return SearchCriteriaService.getResources(
            cacheId,
            pageNum,
            pageSize,
            filter,
            filterFavorites,
            domainFilter,
            customFilterValue,
            true,
        ); //extra param for include quantity
    }

    @Timeout
    public static getResourceCustomAttributes() {
        return SearchCriteriaService.getCustomAttributeItems(6, true, null);
    }

    //contacts
    @Timeout
    @Cache({ immutable: true, targetName: "SearchCriteriaService", expireTimeMs: 60000 })
    public static getContacts(
        cacheId: number,
        pageNum: number,
        pageSize: number,
        filter: any,
        filterFavorites: any,
        domainFilter: any,
        customFilterValue: any,
    ) {
        return DataAccess.get(
            SearchCriteriaService.composeUrl(
                SearchCriteriaService.handlePagination(
                    cacheId,
                    pageNum,
                    pageSize,
                    filter,
                    filterFavorites,
                    domainFilter,
                    customFilterValue,
                    "/contact/contacts.json",
                ),
                "SearchCriteriaService.getContacts",
                undefined,
                undefined,
                undefined,
                undefined,
            ),
        ).then(function (data) {
            return SearchCriteriaService.setItemTypeId(data || [], 3);
        }, SearchCriteriaService.errorHandler);
    }

    @Timeout
    public static getContactCustomAttributes() {
        return SearchCriteriaService.getCustomAttributeItems(3, true, null);
    }

    //misc
    @Timeout
    public static getDaysOfWeek() {
        return jSith.when([
            { itemId: 1, itemName: "MO WE FR" },
            { itemId: 1, itemName: "MO WE" },
            { itemId: 1, itemName: "TU TH" },
            { itemId: 1, itemName: "WE FR" },
            { itemId: 1, itemName: "FR" },
        ]);
    }

    /**
     * cached by ServiceCache; used by s25-search-criteria to keep multiselect bean details
     *
     * @param id
     * @param data
     */

    @Timeout
    @Cache({ targetName: "SearchCriteriaService" })
    public static getMultiselectBean(componentId: number, beanName: any) {
        // note: return an empty object here which will be CACHED and used to initialize s25-multiselect module
        // then any updates to this empty modelBean will be kept in the CACHE
        return {};
    }
    /**
     * cached as 'getMultiselectBean' to refresh the cache
     * @param componentId
     * @param beanName
     * @returns {*}
     */

    @Timeout
    @Cache({ targetName: "SearchCriteriaService", cachedMethod: "getMultiselectBean" })
    public static refreshMultiselectBean(componentId: number, beanName: any) {
        return SearchCriteriaService.getMultiselectBean(componentId, beanName);
    }

    @Timeout
    public static getObjectCategories(
        itemTypeId: any,
        useSecurity?: number,
        eventTypeId?: number,
        omitReadOnly?: boolean,
    ) {
        itemTypeId = parseInt(itemTypeId);
        switch (itemTypeId) {
            case 4:
                return SearchCriteriaService.getSpaceCategories(omitReadOnly);
            case 6:
                return SearchCriteriaService.getResourceCategories();
            case 2:
                return SearchCriteriaService.getOrganizationCategories();
            case 1:
                return SearchCriteriaService.getEventCategories(useSecurity, eventTypeId, omitReadOnly);
        }
    }

    @Timeout
    public static getManualTemplates(useSecurity: boolean = false, omitReadOnly?: boolean) {
        return BpeService.getManualTemplates(!useSecurity, omitReadOnly);
    }

    @Timeout
    @Cache({ immutable: true, targetName: "SearchCriteriaService" })
    public static getBuildings() {
        return BuildingService.getBuildings
            .apply(this, [].slice.call(arguments))
            .then((buildings: Awaited<ReturnType<typeof BuildingService.getBuildings>>) => {
                return buildings.map((b) => {
                    return {
                        itemId: b.id,
                        itemName: b.buildingName,
                    };
                });
            });
    }

    @Timeout
    @Cache({ immutable: true, targetName: "SearchCriteriaService" })
    public static getStandardSchedules() {
        return StandardScheduleService.getStandardScheduleList(4).then((data) => {
            return S25Util.array.forceArray(data).map((item) => {
                return { itemId: item.id, itemName: item.name };
            });
        });
    }

    @Timeout
    public static async getQuestionItems(seasonId: number) {
        return (await SearchCriteriaService.getAllQuestions(seasonId))
            .filter(
                (q) =>
                    q.questionLevel &&
                    ["PUBLIC", "SEARCH_COLUMN"].includes(q.questionLevel) &&
                    q.questionType &&
                    ["SELECT", "MULTISELECT"].includes(q.questionType),
            )
            .map(SearchCriteriaService.questionMap);
    }

    @Timeout
    public static async getNonTextQuestionItems(seasonId: number) {
        return (await SearchCriteriaService.getAllQuestions(seasonId))
            .filter((q) => q.questionType && ["SELECT", "MULTISELECT"].includes(q.questionType))
            .map(SearchCriteriaService.questionMap);
    }

    @Timeout
    public static async getAllQuestionItems(seasonId: number) {
        return (await SearchCriteriaService.getAllQuestions(seasonId)).map(SearchCriteriaService.questionMap);
    }

    @Timeout
    public static async getAllIngestionPoints() {
        const points = await DataIngestionService.getIngestionPoints();
        return points.map((point) => ({ itemId: point.ingestionPointId!, itemName: point.ingestionPointName }));
    }

    /**
     * Connect25: batch-resolve election position titles by id. Delegates to {@link ElectionApi.getPositionTitles}.
     */
    public static async getElectionPositions(positionIds: number[]): Promise<S25ItemI[]> {
        return ElectionApi.getPositionTitles(positionIds);
    }

    /**
     * Search-criteria: all non-deleted elections’ positions; labels "position — election" via {@link ElectionApi.searchPositionsForInstanceSearchCriteria}.
     */
    public static async getElectionPositionSearchList(): Promise<Rules.SourceItem[]> {
        const items = await ElectionApi.searchPositionsForInstanceSearchCriteria();
        return (items || []).map((row) => ({
            itemId: Number(row.itemId),
            itemName: row.itemName ?? "",
        }));
    }

    /** Non-deleted Connect25 elections for search-criteria dropdowns (id + name). */
    public static async getElectionsSearchList(): Promise<Rules.SourceItem[]> {
        const rows = await ElectionApi.listElections();
        const list = S25Util.array.forceArray(rows);
        list.sort((a, b) => (a.name || "").localeCompare(b.name || "", undefined, { sensitivity: "base" }));
        return list
            .filter((e) => e.electionId != null && !e.deleted)
            .map((e) => ({
                itemId: Number(e.electionId),
                itemName: (e.name && e.name.trim()) || `Election ${e.electionId}`,
            }));
    }

    /**
     * Search-criteria: one election’s positions (same as {@link ElectionApi.listPositions}).
     */
    public static async getElectionPositionSearchListForElection(
        electionIdRaw?: string | number,
    ): Promise<Rules.SourceItem[]> {
        const raw = electionIdRaw == null || electionIdRaw === "" ? "" : String(electionIdRaw).trim();
        const electionId = S25Util.parseInt(raw);
        if (!raw || !Number.isFinite(electionId) || electionId <= 0) {
            return [];
        }
        const rows = await ElectionApi.listPositions(electionId);
        const list = S25Util.array.forceArray(rows);
        list.sort((a, b) => (a.sortOrder ?? 0) - (b.sortOrder ?? 0));
        return list
            .filter((r) => r.electionPositionId != null)
            .map((r) => ({
                itemId: Number(r.electionPositionId),
                itemName: (r.positionTitle && r.positionTitle.trim()) || `Position ${r.electionPositionId}`,
            }));
    }

    @Timeout
    public static async getAllIngestionFieldItems(ingestionPointId: number) {
        return (await SearchCriteriaService.getAllIngestionFields(ingestionPointId)).map(
            SearchCriteriaService.ingestionFieldMap,
        );
    }

    @Timeout
    public static async getAllWorkflowProcessItems() {
        return (await SearchCriteriaService.getAllWorkflowProcesses()).map(SearchCriteriaService.workflowProcessMap);
    }

    @Timeout
    public static async getAllIngestionFields(ingestionPointId: number) {
        const fields = await DataIngestionService.getFieldDefinitions(ingestionPointId);
        fields.sort(S25Util.shallowSort("sortOrder", true));
        fields.forEach((f) => {
            (f.options || []).sort(S25Util.shallowSort("sortOrder", true));
        });
        return fields;
    }

    @Timeout
    public static async getAllWorkflowProcesses() {
        return WorkflowService.getWorkflowProcesses(true);
    }

    @Timeout
    public static async getAllQuestions(seasonId: number) {
        const questions = await StudentHousingService.getQuestions(seasonId);
        questions.sort(S25Util.shallowSort("sortOrder", true));
        questions.forEach((q) => {
            (q.options || []).sort(S25Util.shallowSort("sortOrder", true));
        });
        return questions;
    }

    @Timeout
    public static async getMatchGroups(seasonId: number) {
        return (await StudentHousingService.getMatchGroups(seasonId)).map((group) => {
            return {
                itemId: group.matchGroupId!,
                itemName: group.matchGroup,
                val: group.matchGroup,
            };
        });
    }

    private static questionMap(question: Question) {
        return {
            itemId: question.questionId!,
            itemName: question.question,
            itemTypeId: 7001,
            children: (question.options || []).map((opt) => {
                return {
                    itemId: opt.option!,
                    itemName: opt.option!,
                };
            }),
        };
    }

    private static ingestionFieldMap(field: IngestionFieldDefinition): Rules.SourceItem {
        let objectType = null;

        // Handle special field types first
        if (field.fieldType === "EVENT_TYPE") {
            objectType = Rules.objectType.EventType;
        } else if (field.fieldType === "ORGANIZATION" || field.fieldType === "ORGANIZATION_MULTISELECT") {
            objectType = Rules.objectType.Organization;
        } else if (field.fieldType === "CATEGORIES") {
            objectType = Rules.objectType.EventCategory;
        } else if (field.fieldType === "REQUIREMENTS") {
            objectType = Rules.objectType.Requirement;
        } else if (field.fieldType === "REGISTRANTS") {
            objectType = Rules.objectType.Registrants;
        } else if (field.fieldType === "ORG_CATEGORIES") {
            objectType = Rules.objectType.OrgCategory;
        } else if (field.fieldType === "CONTACT_ROLE") {
            objectType = Rules.objectType.ContactRole;
        } else if (field.fieldType === "ORG_CONTACT_ROLE") {
            objectType = Rules.objectType.OrgContactRole;
        } else if (field.fieldType === "DURATION") {
            objectType = Rules.objectType.Duration;
        } else if (field.fieldType === "ORG_TYPE") {
            objectType = Rules.objectType.OrgType;
        } else if (field.fieldType === "ORG_RATING") {
            objectType = Rules.objectType.OrgRating;
        } else if (field.fieldType === "SECURITY_GROUP") {
            objectType = Rules.objectType.SecurityGroupField;
        } else if (field.fieldType === "ELECTION_POSITION") {
            objectType = Rules.objectType.ElectionPosition;
        } else {
            // Handle regular field types based on answerType
            switch (field.answerType) {
                case "STRING":
                    objectType = Rules.objectType.Text;
                    break;
                case "LARGE_STRING":
                    objectType = Rules.objectType.LongText;
                    break;
                case "INTEGER":
                    objectType = Rules.objectType.Integer;
                    break;
                case "FLOAT":
                    objectType = Rules.objectType.Float;
                    break;
                case "BOOLEAN":
                    objectType = Rules.objectType.Boolean;
                    break;
                case "DATETIME":
                    objectType = Rules.objectType.DateTime;
                    break;
                default:
                    objectType = Rules.objectType.Text;
                    break;
            }
        }

        return {
            itemId: field.fieldDefinitionId!,
            itemName: field.fieldLabel,
            itemTypeId: INGESTION_FIELD_TYPE_ID, // type for ingestion fields
            objectType: objectType,
            children: (field.options || []).map((opt: any) => {
                return {
                    itemId: opt.option,
                    itemName: opt.option,
                };
            }),
        };
    }

    private static workflowProcessMap(process: any): Rules.SourceItem {
        return {
            itemId: process.workflowProcessId,
            itemName: process.workflowProcessName,
            itemTypeId: Workflow.WORKFLOW_PROCESS_TYPE_ID, // type for workflow processes
        };
    }

    // Budgets
    @Timeout
    @Cache({ immutable: true, targetName: "SearchCriteriaService" })
    public static async getBudgets(
        useSecurity?: boolean,
        activeOnly?: boolean,
        omitReadOnly?: boolean,
        organizationId?: number,
    ): Promise<Budget.BudgetDropdownItem[]> {
        if (!organizationId) {
            return [];
        }
        try {
            const budgets = await BudgetService.getAllOrgBudgets(organizationId);
            return budgets.map((budget: Budget.OrgBudget) => ({
                itemId: budget.budgetId,
                itemName: budget.name,
                itemTypeId: Budget.BUDGET_ITEM_TYPE_ID,
                startDate: S25Util.date.toS25ISODateTimeStr(budget.startDate),
                endDate: S25Util.date.toS25ISODateTimeStr(budget.endDate),
            }));
        } catch (error) {
            return SearchCriteriaService.errorHandler(error);
        }
    }

    // Budget Plan Types
    @Timeout
    @Cache({ immutable: true, targetName: "SearchCriteriaService" })
    public static async getBudgetPlanTypes() {
        return Budget.BudgetPlanTypes.map((planType) => ({
            itemId: planType.itemId,
            itemName: planType.itemName,
            itemTypeId: Budget.BUDGET_PLAN_ITEM_TYPE_ID,
        }));
    }

    // Fiscal Years
    @Timeout
    @Cache({ immutable: true, targetName: "SearchCriteriaService" })
    public static async getFiscalYears() {
        try {
            const fiscalYears = await BudgetService.getFiscalYears();
            return fiscalYears.map((fiscalYear) => ({
                itemId: fiscalYear.fiscalYearId,
                itemName: fiscalYear.name,
                itemTypeId: Budget.BUDGET_ITEM_TYPE_ID,
            }));
        } catch (error) {
            return SearchCriteriaService.errorHandler(error);
        }
    }
}

export type SearchCriteriaResponse = {
    searchCriteria: {
        engine: "accl";
        item: SearchCriteriaItem[];
    };
};

export type SearchCriteriaItem = {
    itemId: number;
    itemName: string;
    isInactive?: NumericalBoolean;
    tags?: MasterDefTag.MdTag[];
    sort_order?: number;
};
